import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { ReactNode } from "react";
import {
  User,
  FileText,
  Package,
  DollarSign,
  ClipboardList,
  Wrench,
  ExternalLink,
  Eye,
  MessageSquare,
  Send,
  Trash2,
} from "lucide-react";
import { usePedidoDetalhes } from "@/hooks/usePedidos";
import {
  usePedidoComentarios,
  useCriarPedidoComentario,
  useExcluirPedidoComentario,
} from "@/hooks/usePedidoComentarios";
import { useAuth } from "@/hooks/useAuth";
import {
  CONFIRMACAO_VENDA_LABELS,
  SITUACAO_RENOVACAO_LABELS,
  TIPO_NEGOCIO_LABELS,
  formatNumeroCicloVenda,
  getConfirmacaoVendaBadgeClass,
  getSituacaoRenovacaoBadgeClass,
  getTipoNegocioBadgeClass,
  type ConfirmacaoVendaStatus,
  type SituacaoRenovacaoPedido,
  type TipoNegocioPedido,
} from "@/lib/pedidosCiclo";
import { LIMPEZA_STATUS_LABELS, getLimpezaStatusBadgeClass, type LimpezaStatus } from "@/lib/planoLimpeza";
import { parseNotasFiscais } from "@/lib/transacaoNotasFiscais";
import { openStorageUrl } from "@/lib/storageUrl";
import { toast } from "sonner";

interface DetalhesPedidoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pedidoId: number | null;
}

const formatMoney = (valor?: number | null) => {
  if (valor == null || Number.isNaN(Number(valor))) return "—";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(valor));
};

const formatDate = (data?: string | null) => {
  if (!data) return "—";
  const d = new Date(data.includes("T") ? data : `${data}T12:00:00`);
  if (Number.isNaN(d.getTime())) return data;
  return d.toLocaleDateString("pt-BR");
};

const formatDateTime = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.toLocaleDateString("pt-BR")} ${d.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const getStatusColor = (status?: string) => {
  switch (status?.toLowerCase()) {
    case "aprovado":
    case "finalizado":
    case "ativo":
    case "pago":
      return "bg-green-100 text-green-800";
    case "em_contrato":
    case "pendente":
      return "bg-yellow-100 text-yellow-800";
    case "cancelado":
    case "atrasado":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <Label className="text-xs text-muted-foreground font-medium">{label}</Label>
      <div className="text-sm text-gray-900 mt-0.5 break-words">{children ?? "—"}</div>
    </div>
  );
}

/** Destaque laranja (mesmo formato do badge de tipo de negócio) quando há valor. */
function DestaqueEmpresa({ value }: { value?: string | number | null }) {
  const texto = value != null ? String(value).trim() : "";
  if (!texto) return <>{"—"}</>;
  return (
    <Badge className="bg-orange-400 text-white hover:bg-orange-400 border-transparent font-medium">
      {texto}
    </Badge>
  );
}

const ABAS = [
  { value: "resumo", label: "Resumo", icon: ClipboardList },
  { value: "cliente", label: "Cliente", icon: User },
  { value: "plano", label: "Plano", icon: FileText },
  { value: "equipamentos", label: "Equipamentos", icon: Wrench },
  { value: "contrato", label: "Contrato", icon: Package },
  { value: "financeiro", label: "Financeiro", icon: DollarSign },
  { value: "comentarios", label: "Comentários", icon: MessageSquare },
] as const;

const tabTriggerClass =
  "flex h-11 w-full min-w-0 flex-col items-center justify-center gap-0.5 rounded-md px-1 py-1 text-[10px] font-medium leading-tight whitespace-normal sm:flex-row sm:gap-1 sm:text-xs";

const tabPanelClass = "mt-4 h-[420px] overflow-y-auto space-y-4 pr-1";

export function DetalhesPedidoDialog({ open, onOpenChange, pedidoId }: DetalhesPedidoDialogProps) {
  const { data: pedido, isLoading, error } = usePedidoDetalhes(pedidoId, open && !!pedidoId);
  const { data: comentarios = [], isLoading: isLoadingComentarios } = usePedidoComentarios(
    pedidoId,
    open && !!pedidoId
  );
  const criarComentario = useCriarPedidoComentario();
  const excluirComentario = useExcluirPedidoComentario();
  const { isAdmin, isTi } = useAuth();
  const [novoComentario, setNovoComentario] = useState("");
  const podeApagarComentario = isAdmin() || isTi();

  const numero =
    pedido?.numero_pedido ||
    (pedido?.id != null ? `#${String(pedido.id).padStart(6, "0")}` : "—");

  const valorProjeto =
    pedido?.projeto?.valor_projeto && Number(pedido.projeto.valor_projeto) > 0
      ? Number(pedido.projeto.valor_projeto)
      : pedido?.orcamento?.valor_total != null
        ? Number(pedido.orcamento.valor_total)
        : pedido?.valor_total != null
          ? Number(pedido.valor_total)
          : null;

  const enderecoParts = [
    pedido?.cliente?.endereco,
    pedido?.cliente?.numero ? `nº ${pedido.cliente.numero}` : null,
    pedido?.cliente?.bairro,
  ].filter(Boolean);
  const cidadeUf = [pedido?.cliente?.cidade, pedido?.cliente?.estado].filter(Boolean).join(" / ");

  const handleEnviarComentario = async () => {
    if (!pedidoId || !novoComentario.trim()) {
      toast.error("Escreva um comentário.");
      return;
    }
    try {
      await criarComentario.mutateAsync({ pedidoId, conteudo: novoComentario });
      setNovoComentario("");
      toast.success("Comentário adicionado");
    } catch (e) {
      toast.error((e as Error)?.message || "Erro ao salvar comentário");
    }
  };

  const handleExcluirComentario = async (comentarioId: number) => {
    if (!pedidoId) return;
    try {
      await excluirComentario.mutateAsync({ id: comentarioId, pedidoId });
      toast.success("Comentário excluído");
    } catch (e) {
      toast.error((e as Error)?.message || "Erro ao excluir comentário");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col overflow-hidden gap-3">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex flex-wrap items-center gap-2">
            <span>Pedido {numero}</span>
            {pedido?.status && (
              <Badge className={getStatusColor(pedido.status)}>
                {pedido.status.charAt(0).toUpperCase() + pedido.status.slice(1).replace(/_/g, " ")}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="py-12 text-center text-muted-foreground text-sm">Carregando detalhes...</div>
        )}

        {error && (
          <div className="py-8 text-center text-red-600 text-sm">
            Erro ao carregar o pedido. Tente novamente.
          </div>
        )}

        {!isLoading && !error && pedido && (
          <Tabs defaultValue="resumo" className="w-full min-h-0 flex-1 flex flex-col">
            <TabsList className="grid h-auto w-full shrink-0 grid-cols-4 gap-1 rounded-md bg-muted p-1 sm:grid-cols-7">
              {ABAS.map(({ value, label, icon: Icon }) => (
                <TabsTrigger key={value} value={value} className={tabTriggerClass}>
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  <span className="block w-full truncate text-center">{label}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="resumo" className={tabPanelClass}>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <Field label="Número">{numero}</Field>
                <Field label="Data do pedido">{formatDate(pedido.data_pedido)}</Field>
                <Field label="Prazo de entrega">
                  {pedido.prazo_entrega != null ? `${pedido.prazo_entrega} dias` : "—"}
                </Field>
                <Field label="Ciclo">
                  {formatNumeroCicloVenda(pedido.numero_ciclo, pedido.tipo_negocio)}
                </Field>
                <Field label="Tipo de negócio">
                  {pedido.tipo_negocio ? (
                    <Badge className={getTipoNegocioBadgeClass(pedido.tipo_negocio)}>
                      {TIPO_NEGOCIO_LABELS[pedido.tipo_negocio as TipoNegocioPedido] ||
                        pedido.tipo_negocio}
                    </Badge>
                  ) : (
                    "—"
                  )}
                </Field>
                <Field label="Situação renovação">
                  {pedido.situacao_renovacao ? (
                    <Badge className={getSituacaoRenovacaoBadgeClass(pedido.situacao_renovacao)}>
                      {SITUACAO_RENOVACAO_LABELS[
                        pedido.situacao_renovacao as SituacaoRenovacaoPedido
                      ] || pedido.situacao_renovacao}
                    </Badge>
                  ) : (
                    "—"
                  )}
                </Field>
                <Field label="Confirmação de venda">
                  {pedido.confirmacao_status ? (
                    <Badge className={getConfirmacaoVendaBadgeClass(pedido.confirmacao_status)}>
                      {CONFIRMACAO_VENDA_LABELS[
                        pedido.confirmacao_status as ConfirmacaoVendaStatus
                      ] || pedido.confirmacao_status}
                    </Badge>
                  ) : (
                    "Sem financeiro"
                  )}
                </Field>
                <Field label="Limpeza">
                  {pedido.limpeza_status ? (
                    <Badge className={getLimpezaStatusBadgeClass(pedido.limpeza_status as LimpezaStatus)}>
                      {LIMPEZA_STATUS_LABELS[pedido.limpeza_status as LimpezaStatus] ||
                        pedido.limpeza_status}
                    </Badge>
                  ) : (
                    "—"
                  )}
                </Field>
                <Field label="Criado por">
                  {pedido.vendedor?.nome || pedido.vendedor?.email || "—"}
                </Field>
                <Field label="Pedido anterior">
                  {pedido.pedido_anterior
                    ? pedido.pedido_anterior.numero_pedido ||
                      `#${String(pedido.pedido_anterior.id).padStart(6, "0")}`
                    : "—"}
                </Field>
                <Field label="Valor do projeto">{formatMoney(valorProjeto)}</Field>
                <Field label="Valor do seguro">
                  {formatMoney(pedido.orcamento?.valor_seguro)}
                </Field>
                <Field label="Valor total">{formatMoney(pedido.valor_total)}</Field>
                <Field label="Empresa responsável">
                  <DestaqueEmpresa value={pedido.projeto?.empresa_responsavel} />
                </Field>
                <Field label="ID do projeto na empresa">
                  <DestaqueEmpresa
                    value={
                      pedido.projeto?.empresa_projeto_externo_id ||
                      pedido.projeto?.id_projeto_elevo
                    }
                  />
                </Field>
              </div>
              {pedido.projeto && (
                <div className="rounded-md border p-3 space-y-2">
                  <p className="text-sm font-medium">Projeto</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <Field label="ID interno">{pedido.projeto.id}</Field>
                    <Field label="Status">{pedido.projeto.status || "—"}</Field>
                    <Field label="Painéis">{pedido.projeto.total_paineis ?? "—"}</Field>
                  </div>
                  {pedido.projeto.observacoes && (
                    <Field label="Observações do projeto">{pedido.projeto.observacoes}</Field>
                  )}
                </div>
              )}
              {pedido.observacoes && <Field label="Observações do pedido">{pedido.observacoes}</Field>}
            </TabsContent>

            <TabsContent value="cliente" className={tabPanelClass}>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <Field label="Nome">{pedido.cliente?.nome || "—"}</Field>
                <Field label="CPF/CNPJ">{pedido.cliente?.cpf_cnpj || "—"}</Field>
                <Field label="E-mail">{pedido.cliente?.email || "—"}</Field>
                <Field label="Telefone">{pedido.cliente?.telefone || "—"}</Field>
                <Field label="CEP">{pedido.cliente?.cep || "—"}</Field>
                <Field label="Cidade / UF">{cidadeUf || "—"}</Field>
                <div className="sm:col-span-2 md:col-span-3">
                  <Field label="Endereço">
                    {enderecoParts.length > 0 ? enderecoParts.join(", ") : "—"}
                  </Field>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="plano" className={tabPanelClass}>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <Field label="Plano">
                  {pedido.orcamento?.plano?.nome ||
                    (pedido.orcamento?.plano_id != null
                      ? String(pedido.orcamento.plano_id)
                      : "—")}
                </Field>
                <Field label="Inclui limpeza">
                  {pedido.orcamento?.plano?.inclui_limpeza ? "Sim" : "Não"}
                </Field>
                <Field label="Vigência (anos)">
                  {pedido.orcamento?.vigencia_anos ??
                    (pedido.orcamento?.plano?.vigencia_meses
                      ? Math.floor(pedido.orcamento.plano.vigencia_meses / 12)
                      : "—")}
                </Field>
                <Field label="Valor do seguro">
                  {formatMoney(pedido.orcamento?.valor_seguro)}
                </Field>
                <Field label="Valor do orçamento">
                  {formatMoney(pedido.orcamento?.valor_total)}
                </Field>
                <Field label="Quantidade de painéis">
                  {pedido.orcamento?.quantidade_placas ?? "—"}
                </Field>
                <Field label="Forma de pagamento">
                  {pedido.forma_pagamento_nome || pedido.orcamento?.forma_pagamento || "—"}
                </Field>
                <Field label="Parcelas">
                  {pedido.orcamento?.numero_parcelas ??
                    pedido.orcamento?.parcelas_cartao ??
                    "—"}
                </Field>
                <Field label="ID do orçamento">
                  {pedido.orcamento?.id != null
                    ? `#${String(pedido.orcamento.id).padStart(6, "0")}`
                    : "—"}
                </Field>
              </div>
            </TabsContent>

            <TabsContent value="equipamentos" className={tabPanelClass}>
              {!pedido.equipamentos?.length ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  Nenhum equipamento vinculado.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/40">
                        <th className="text-left p-2 font-medium">Tipo</th>
                        <th className="text-left p-2 font-medium">Marca</th>
                        <th className="text-left p-2 font-medium">Modelo</th>
                        <th className="text-center p-2 font-medium">Qtd</th>
                        <th className="text-left p-2 font-medium">Potência</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pedido.equipamentos.map((eq, idx) => (
                        <tr key={eq.id ?? idx} className="border-b last:border-0">
                          <td className="p-2">{eq.equipamento?.tipo || "—"}</td>
                          <td className="p-2">{eq.equipamento?.marca || "—"}</td>
                          <td className="p-2">{eq.equipamento?.modelo || "—"}</td>
                          <td className="p-2 text-center">{eq.quantidade ?? "—"}</td>
                          <td className="p-2">{eq.equipamento?.potencia || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="contrato" className={tabPanelClass}>
              {!pedido.contratos?.length ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Sem contrato.</p>
              ) : (
                <div className="space-y-3">
                  {pedido.contratos.map((c) => (
                    <div key={c.id} className="rounded-md border p-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      <Field label="Número">{c.numero_contrato}</Field>
                      <Field label="Status">
                        <Badge className={getStatusColor(c.status)}>{c.status}</Badge>
                      </Field>
                      <Field label="Valor">{formatMoney(c.valor)}</Field>
                      <Field label="Início">{formatDate(c.data_inicio)}</Field>
                      <Field label="Fim">{formatDate(c.data_fim)}</Field>
                      <Field label="Seguro ativado">
                        {c.seguro_ativado ? `Sim (${formatDate(c.data_ativacao_seguro)})` : "Não"}
                      </Field>
                      {c.arquivo_pdf_url && (
                        <div className="sm:col-span-2 md:col-span-3">
                          <Button variant="outline" size="sm" asChild className="h-8 text-xs">
                            <a href={c.arquivo_pdf_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                              {c.arquivo_pdf_name || "Abrir PDF do contrato"}
                            </a>
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="financeiro" className={tabPanelClass}>
              <div>
                <p className="text-sm font-medium mb-2">Transações financeiras</p>
                {!pedido.transacoes?.length ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    Nenhuma transação gerada.
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-md border">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/40">
                          <th className="text-left p-2 font-medium">Tipo</th>
                          <th className="text-center p-2 font-medium">Parcela</th>
                          <th className="text-right p-2 font-medium">Valor</th>
                          <th className="text-left p-2 font-medium">Vencimento</th>
                          <th className="text-left p-2 font-medium">Pagamento</th>
                          <th className="text-left p-2 font-medium">Status</th>
                          <th className="text-left p-2 font-medium">Forma</th>
                          <th className="text-center p-2 font-medium">Comprovante</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pedido.transacoes.map((t) => {
                          const anexos = parseNotasFiscais(t);
                          return (
                            <tr key={t.id} className="border-b last:border-0">
                              <td className="p-2 capitalize">{t.tipo}</td>
                              <td className="p-2 text-center">
                                {t.numero_parcela != null
                                  ? `${t.numero_parcela}/${t.total_parcelas ?? "?"}`
                                  : "—"}
                              </td>
                              <td className="p-2 text-right">{formatMoney(t.valor)}</td>
                              <td className="p-2">{formatDate(t.data_vencimento)}</td>
                              <td className="p-2">{formatDate(t.data_pagamento)}</td>
                              <td className="p-2">
                                <Badge className={getStatusColor(t.status)}>{t.status}</Badge>
                              </td>
                              <td className="p-2">{t.forma_pagamento || "—"}</td>
                              <td className="p-2 text-center">
                                {anexos.length > 0 ? (
                                  <div className="flex flex-col items-center gap-1">
                                    {anexos.map((nota, idx) => (
                                      <Button
                                        key={`${nota.url}-${idx}`}
                                        variant="outline"
                                        size="sm"
                                        className="h-7 px-2 text-[11px]"
                                        title={nota.nome}
                                        onClick={() => void openStorageUrl(nota.url)}
                                      >
                                        <Eye className="h-3.5 w-3.5 mr-1" />
                                        {anexos.length > 1 ? `Ver ${idx + 1}` : "Ver"}
                                      </Button>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {!!pedido.parcelas?.length && (
                <div>
                  <p className="text-sm font-medium mb-2">Parcelas</p>
                  <div className="overflow-x-auto rounded-md border">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/40">
                          <th className="text-center p-2 font-medium">Parcela</th>
                          <th className="text-right p-2 font-medium">Valor</th>
                          <th className="text-left p-2 font-medium">Vencimento</th>
                          <th className="text-left p-2 font-medium">Pagamento</th>
                          <th className="text-left p-2 font-medium">Status</th>
                          <th className="text-left p-2 font-medium">Forma</th>
                          <th className="text-center p-2 font-medium">Comprovante</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pedido.parcelas.map((p) => (
                          <tr key={p.id} className="border-b last:border-0">
                            <td className="p-2 text-center">
                              {p.numero_parcela}/{p.total_parcelas}
                            </td>
                            <td className="p-2 text-right">{formatMoney(p.valor)}</td>
                            <td className="p-2">{formatDate(p.data_vencimento)}</td>
                            <td className="p-2">{formatDate(p.data_pagamento)}</td>
                            <td className="p-2">
                              <Badge className={getStatusColor(p.status)}>{p.status}</Badge>
                            </td>
                            <td className="p-2">{p.forma_pagamento || "—"}</td>
                            <td className="p-2 text-center">
                              {p.comprovante_pagamento_url ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 px-2 text-[11px]"
                                  title={p.comprovante_pagamento_name || "Comprovante"}
                                  onClick={() => void openStorageUrl(p.comprovante_pagamento_url!)}
                                >
                                  <Eye className="h-3.5 w-3.5 mr-1" />
                                  Ver
                                </Button>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="comentarios" className={tabPanelClass}>
              <div className="space-y-2">
                <Textarea
                  value={novoComentario}
                  onChange={(e) => setNovoComentario(e.target.value)}
                  placeholder="Escreva um comentário sobre este pedido..."
                  rows={3}
                />
                <div className="flex justify-end">
                  <Button
                    onClick={handleEnviarComentario}
                    disabled={criarComentario.isPending || !novoComentario.trim()}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {criarComentario.isPending ? "Enviando..." : "Comentar"}
                  </Button>
                </div>
              </div>

              {isLoadingComentarios ? (
                <p className="text-sm text-muted-foreground">Carregando comentários...</p>
              ) : comentarios.length === 0 ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
                  <p className="text-sm text-gray-600">Nenhum comentário ainda.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {comentarios.map((c) => (
                    <div key={c.id} className="rounded-lg border border-gray-200 bg-white p-3">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-900">
                          {c.usuario?.nome || c.usuario?.email || "Usuário"}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">{formatDateTime(c.created_at)}</span>
                          {podeApagarComentario && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Excluir comentário"
                              disabled={excluirComentario.isPending}
                              onClick={() => handleExcluirComentario(c.id)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{c.conteudo}</p>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        <div className="flex justify-end shrink-0 pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
